import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ProductImage } from 'src/components/product-image';
import { Translate } from 'src/components/translate';
import type { ChangeComparisonSelectionSource } from '../../areas/product-compare/tracking';
import { trackComparisonLinkClick } from '../../areas/product-compare/tracking';
import * as S from './styles';
import type { ProductSelectedToCompare } from '../../hooks/compare-products-selection';
import { IconChevronDown } from '@prisjakt/ui/icon/chevron-down';
import { IconChevronUp } from '@prisjakt/ui/icon/chevron-up';
import { IconClose } from '@prisjakt/ui/icon/close';
import { IconCompare } from '@prisjakt/ui/icon/compare';
import {
  ButtonIcon,
  ButtonText,
  OutlinedButton,
} from '@prisjakt/ui/button/variants';
import { useSelector } from 'react-redux';
import type { AppState } from 'src/reducers';

type Props = {
  products: ProductSelectedToCompare[];
  compareUrl?: string;
  onEmpty: () => void;
  onProductDelete: (
    productId: number,
    source: ChangeComparisonSelectionSource
  ) => void;
};

const CompareProductsBasket = ({
  products,
  compareUrl,
  onEmpty,
  onProductDelete,
}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = useCallback(() => setExpanded(!expanded), [expanded]);
  const compare = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      e.stopPropagation();
      trackComparisonLinkClick(products.length);
    },
    [products.length]
  );
  const productsWithImage = useMemo(
    () =>
      products.map(product => ({
        ...product,
        image: (
          <ProductImage name={product.name} src={product.media.first || ''} />
        ),
      })),
    [products]
  );

  const isSidebarOpen = useSelector<AppState, boolean>(
    state =>
      state.areas.sidebar.tab != null && state.areas.sidebar.tab !== 'none'
  );

  return (
    <S.Container sidebarOpen={isSidebarOpen}>
      <S.Header onClick={toggleExpanded}>
        <S.FlexWrapper>
          <S.ExpandIcon>
            {expanded ? <IconChevronDown /> : <IconChevronUp />}
          </S.ExpandIcon>
          {products.length === 0 ? (
            <S.InfoText>
              <Translate domain="product">
                product-compare.choose-products
              </Translate>
            </S.InfoText>
          ) : (
            <S.ProductCount>
              <Translate domain="lists" n={products.length}>
                list.n-products
              </Translate>
            </S.ProductCount>
          )}
        </S.FlexWrapper>
        <OutlinedButton
          onClick={compare}
          container={products.length < 2 ? 'button' : Link}
          disabled // Won't affect the link ☝️
          containerProps={{
            'data-test': 'CompareButton',
            to: compareUrl,
          }}
          style={{ backgroundColor: 'white' }}
        >
          <ButtonIcon>
            <IconCompare size={18} />
          </ButtonIcon>
          <ButtonText>
            <Translate domain="product">product-compare.compare</Translate>
          </ButtonText>
        </OutlinedButton>
      </S.Header>
      <S.ProductWrapper count={products.length} expanded={expanded}>
        <S.InfoBar>
          {products.length < 2 && (
            <S.InfoText>
              <Translate domain="product">
                product-compare.choose-more-products
              </Translate>
            </S.InfoText>
          )}
          <S.ClearButton variant="text" color="link" onClick={onEmpty}>
            <Translate domain="product">
              product-compare.cancel-compare
            </Translate>
          </S.ClearButton>
        </S.InfoBar>
        {productsWithImage.map(product => (
          <CompareProductsBasketRow
            key={product.id}
            product={product}
            onProductDelete={onProductDelete}
          />
        ))}
      </S.ProductWrapper>
    </S.Container>
  );
};

type RowProps = {
  product: ProductSelectedToCompare;
  onProductDelete: (
    productId: number,
    source: ChangeComparisonSelectionSource
  ) => void;
};

const CompareProductsBasketRow = ({ product, onProductDelete }: RowProps) => {
  const productImage = useMemo(
    () => <ProductImage name={product.name} src={product.media.first || ''} />,
    [product.name, product.media.first]
  );
  const onProductClick = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => e.preventDefault(),
    []
  );
  const onDelete = useCallback(
    () => onProductDelete(product.id, 'basket'),
    [onProductDelete, product.id]
  );

  return (
    <S.ProductRow
      key={product.id}
      link={product.pathName}
      onClick={onProductClick}
      name={product.name}
      image={productImage}
      price={product.priceSummary.regular || product.priceSummary.alternative}
      showDivider
    >
      <S.ProductDeleteButton
        size="small"
        variant="icon"
        color="primary"
        icon={<IconClose />}
        onClick={onDelete}
      />
    </S.ProductRow>
  );
};

export default CompareProductsBasket;
